/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

/**
 * Generate a unique ID without uuid dependency
 */
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Corkboard data structure extends the existing notes system.
 * Each corkboard contains:
 * - id: unique identifier
 * - projectId: associated project ID
 * - name: display name
 * - notes: array of note objects (using existing note schema + x, y, width, height)
 * - drawings: array of drawing objects
 * - viewState: pan and zoom state
 * - createdAt: creation timestamp
 * - updatedAt: last update timestamp
 */

const initialState = {
  corkboards: [],
  activeCorkboardId: null,
};

export const corkboardsSlice = createSlice({
  name: 'corkboards',
  initialState,
  reducers: {
    // Corkboard CRUD
    addCorkboard: (state, action) => {
      const newCorkboard = {
        id: action.payload.id || generateId(),
        projectId: action.payload.projectId || null,
        name: action.payload.name || 'Untitled Corkboard',
        notes: action.payload.notes || [],
        drawings: action.payload.drawings || [],
        viewState: action.payload.viewState || {
          panX: 0,
          panY: 0,
          zoom: 1,
        },
        createdAt: action.payload.createdAt || Date.now(),
        updatedAt: Date.now(),
      };
      state.corkboards.unshift(newCorkboard);
    },

    setCorkboards: (state, action) => {
      state.corkboards = action.payload;
    },

    updateCorkboard: (state, action) => {
      const index = state.corkboards.findIndex((cb) => cb.id === action.payload.id);
      if (index !== -1) {
        state.corkboards[index] = {
          ...state.corkboards[index],
          ...action.payload,
          updatedAt: Date.now(),
        };
      }
    },

    removeCorkboard: (state, action) => {
      state.corkboards = state.corkboards.filter((cb) => cb.id !== action.payload);
      if (state.activeCorkboardId === action.payload) {
        state.activeCorkboardId = null;
      }
    },

    setActiveCorkboard: (state, action) => {
      state.activeCorkboardId = action.payload;
    },

    // Note operations within a corkboard
    addNoteToCorkboard: (state, action) => {
      const { corkboardId, note } = action.payload;
      const corkboard = state.corkboards.find((cb) => cb.id === corkboardId);
      if (corkboard) {
        // Extend existing note schema with position/size for corkboard
        const corkboardNote = {
          ...note,
          _id: note._id || note.id || generateId(),
          x: note.x ?? 100,
          y: note.y ?? 100,
          width: note.width ?? 200,
          height: note.height ?? 150,
        };
        corkboard.notes.push(corkboardNote);
        corkboard.updatedAt = Date.now();
      }
    },

    updateNoteInCorkboard: (state, action) => {
      const { corkboardId, noteId, updates } = action.payload;
      const corkboard = state.corkboards.find((cb) => cb.id === corkboardId);
      if (corkboard) {
        const noteIndex = corkboard.notes.findIndex((n) => (n._id || n.id || n.noteId) === noteId);
        if (noteIndex !== -1) {
          corkboard.notes[noteIndex] = {
            ...corkboard.notes[noteIndex],
            ...updates,
          };
          corkboard.updatedAt = Date.now();
        }
      }
    },

    removeNoteFromCorkboard: (state, action) => {
      const { corkboardId, noteId } = action.payload;
      const corkboard = state.corkboards.find((cb) => cb.id === corkboardId);
      if (corkboard) {
        corkboard.notes = corkboard.notes.filter((n) => (n._id || n.id || n.noteId) !== noteId);
        corkboard.updatedAt = Date.now();
      }
    },

    // Drawing operations
    addDrawing: (state, action) => {
      const { corkboardId, drawing } = action.payload;
      const corkboard = state.corkboards.find((cb) => cb.id === corkboardId);
      if (corkboard) {
        corkboard.drawings.push({
          id: drawing.id || generateId(),
          type: drawing.type, // 'freehand' | 'line'
          color: drawing.color || '#000000',
          points: drawing.points || [],
          attachments: drawing.attachments || undefined,
        });
        corkboard.updatedAt = Date.now();
      }
    },

    removeDrawing: (state, action) => {
      const { corkboardId, drawingId } = action.payload;
      const corkboard = state.corkboards.find((cb) => cb.id === corkboardId);
      if (corkboard) {
        corkboard.drawings = corkboard.drawings.filter((d) => d.id !== drawingId);
        corkboard.updatedAt = Date.now();
      }
    },

    updateDrawing: (state, action) => {
      const { corkboardId, drawingId, updates } = action.payload;
      const corkboard = state.corkboards.find((cb) => cb.id === corkboardId);
      if (corkboard) {
        const drawingIndex = corkboard.drawings.findIndex((d) => d.id === drawingId);
        if (drawingIndex !== -1) {
          corkboard.drawings[drawingIndex] = {
            ...corkboard.drawings[drawingIndex],
            ...updates,
          };
          corkboard.updatedAt = Date.now();
        }
      }
    },

    clearDrawings: (state, action) => {
      const { corkboardId } = action.payload;
      const corkboard = state.corkboards.find((cb) => cb.id === corkboardId);
      if (corkboard) {
        corkboard.drawings = [];
        corkboard.updatedAt = Date.now();
      }
    },

    // View state operations
    updateViewState: (state, action) => {
      const { corkboardId, viewState } = action.payload;
      const corkboard = state.corkboards.find((cb) => cb.id === corkboardId);
      if (corkboard) {
        corkboard.viewState = {
          ...corkboard.viewState,
          ...viewState,
        };
      }
    },

    resetViewState: (state, action) => {
      const { corkboardId } = action.payload;
      const corkboard = state.corkboards.find((cb) => cb.id === corkboardId);
      if (corkboard) {
        corkboard.viewState = {
          panX: 0,
          panY: 0,
          zoom: 1,
        };
      }
    },
  },
});

export const {
  addCorkboard,
  setCorkboards,
  updateCorkboard,
  removeCorkboard,
  setActiveCorkboard,
  addNoteToCorkboard,
  updateNoteInCorkboard,
  removeNoteFromCorkboard,
  addDrawing,
  removeDrawing,
  updateDrawing,
  clearDrawings,
  updateViewState,
  resetViewState,
} = corkboardsSlice.actions;

// Selectors
export const selectCorkboards = (state) => state.corkboards.corkboards;
export const selectActiveCorkboardId = (state) => state.corkboards.activeCorkboardId;
export const selectCorkboardById = (id) => (state) =>
  state.corkboards.corkboards.find((cb) => cb.id === id);
export const selectCorkboardsByProject = (projectId) => (state) =>
  state.corkboards.corkboards.filter((cb) => cb.projectId === projectId);

export default corkboardsSlice.reducer;
